import { Stack, Typography } from '@mui/material'
import { AlertRule } from '../types/Alerts'
import { AlertRuleDataGridRow } from './AlertRuleDataGrid'
import { translateAlertRuleName } from '../utils/alert'
import AlertRuleIcon from './AlertRuleIcon'
import { AlertRuleName } from '../enums'
import { Trans, useTranslation } from 'react-i18next'

export interface AlertRuleLabelProps {
  alertRule: AlertRule | AlertRuleDataGridRow | AlertRuleName
}

export default function AlertRuleLabel({ alertRule }: AlertRuleLabelProps) {
  /**
   * The alert rule name.
   */
  const alertRuleName = typeof alertRule === 'string' ? alertRule : alertRule.name

  function isAlertRule(
    rule: AlertRule | AlertRuleDataGridRow | AlertRuleName
  ): rule is AlertRule | AlertRuleDataGridRow {
    return (rule as AlertRule | AlertRuleDataGridRow).name !== undefined
  }

  return (
    <Stack direction="row" alignItems="center" sx={{ height: '100%' }}>
      <Stack direction="row" alignItems="center" gap={3}>
        <AlertRuleIcon color="action" alertRule={alertRuleName} />
        <Stack direction="column" sx={{ py: 1 }}>
          <Typography>{translateAlertRuleName(alertRuleName)}</Typography>
          {isAlertRule(alertRule) && (
            <Typography color="text.secondary" variant="caption">
              <AlertRuleExpressionTranslation rule={alertRule} />
            </Typography>
          )}
        </Stack>
      </Stack>
    </Stack>
  )
}

interface AlertRuleExpressionTranslationProps {
  rule: AlertRule | AlertRuleDataGridRow
}

function AlertRuleExpressionTranslation({ rule }: AlertRuleExpressionTranslationProps) {
  switch (rule.name) {
    case AlertRuleName.HIGH_CURRENT:
    case AlertRuleName.OVERLOADED_CURRENT:
    case AlertRuleName.SHORT_CIRCUIT:
    case AlertRuleName.HIGH_VOLTAGE:
    case AlertRuleName.LOW_VOLTAGE:
    case AlertRuleName.NO_VOLTAGE:
    case AlertRuleName.LOW_SIGNAL:
    case AlertRuleName.WEAK_SIGNAL:
    case AlertRuleName.HIGH_PRESSURE:
    case AlertRuleName.LOW_PRESSURE:
    case AlertRuleName.DEW_POINT:
    case AlertRuleName.CONDENSATION_RISK_EXHAUST:
    case AlertRuleName.CONDENSTATION_RISK_INTAKE:
    case AlertRuleName.EXHAUST_AIR_TOO_COLD:
    case AlertRuleName.EXHAUST_AIR_TOO_HOT:
    case AlertRuleName.RAPID_TEMPERATURE_DROP:
    case AlertRuleName.RAPID_TEMPERATURE_RISE:
      return (
        <Trans
          ns="alert"
          i18nKey={`AlertRule.${rule.name}.expression`}
          components={{
            param1: rule.variables.param1!.value.toString()
          }}
        />
      )

    case AlertRuleName.HIGH_TEMPERATURE:
    case AlertRuleName.LOW_TEMPERATURE:
    case AlertRuleName.HIGH_HUMIDITY:
    case AlertRuleName.LOW_HUMIDITY:
      return (
        <Trans
          ns="alert"
          i18nKey={
            rule?.sensor === undefined || rule?.sensor === 'device'
              ? `AlertRule.Device${rule.name}.expression`
              : `AlertRule.${rule.name}.expression`
          }
          components={{
            param1: rule.variables.param1!.value.toString()
          }}
        />
      )

    case AlertRuleName.NO_HEATING:
    case AlertRuleName.HEATING_WHILE_VALVE_CLOSED:
    case AlertRuleName.TARGET_TEMPERATURE_NOT_REACHED:
      return (
        <Trans
          ns="alert"
          i18nKey={`AlertRule.${rule.name}.expression`}
          components={{
            param1: rule.variables.param1!.value.toString(),
            param2: rule.variables.param2!.value.toString()
          }}
        />
      )

    case AlertRuleName.LOW_BATTERY:
      return (
        <Trans
          ns="alert"
          i18nKey={`AlertRule.${rule.name}.expression`}
          values={{ unit: rule.variables.param1?.unit ?? 'V' }}
          components={{
            param1: rule.variables.param1!.value.toString()
          }}
        />
      )

    case AlertRuleName.DEVICE_OFFLINE:
      return (
        <Trans
          ns="alert"
          i18nKey={`AlertRule.${rule.name}.expression`}
          components={{
            param1:
              rule.variables.param1?.unit === 'Status'
                ? '3'
                : rule.variables.param1!.value.toString()
          }}
        />
      )

    default:
      return null
  }
}
